import { Formik } from 'formik';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import React from 'react';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ADD_PROJECT } from '../../apis/project';
import { AUTHORITY } from '../../constants/common';
import { PROJECT } from '../../constants/routes';
import Button from '../atoms/button';
import Modal from '../organisms/modal';

const validationSchema = Yup.object().shape({
  projectName: Yup.string().required('Project name is required.'),
  projectKey: Yup.string()
    .required('Project key is required.')
    .matches(/^[A-Z]{2,4}$/, 'Project key must be uppercase letters and 2 to 4 characters long')
    .min(2, 'Project key must be 2 to 4 characters long')
    .max(4, 'Project key must be 2 to 4 characters long'),
  description: Yup.string().max(255, 'Maximum 255 characters allowed'),
});

const ModalContent = ({ values, errors, touched, handleChange, handleBlur }) => (
  <>
    <div className="custom-form-group">
      <Typography className="custom-label" variant="caption" display="block" gutterBottom>
        Project Name
      </Typography>
      <TextField
        className="custom-form-input"
        margin="dense"
        id="projectName"
        type="text"
        placeholder="Enter Project name"
        fullWidth
        required
        name="projectName"
        value={values.projectName || ''}
        error={errors.projectName && touched.projectName}
        helperText={touched.projectName && errors.projectName}
        onChange={handleChange}
        onBlur={handleBlur}
        autoComplete="off"
      />
    </div>
    <div className="custom-form-group">
      <Typography className="custom-form-label" variant="caption" display="block" gutterBottom>
        Project Key
      </Typography>
      <TextField
        className="custom-form-input"
        id="projectKey"
        type="text"
        fullWidth
        placeholder="Enter Project key"
        required
        name="projectKey"
        value={values.projectKey || ''}
        error={errors.projectKey && touched.projectKey}
        helperText={touched.projectKey && errors.projectKey}
        onChange={handleChange}
        onBlur={handleBlur}
        autoComplete="off"
      />
    </div>
    <div className="custom-form-group">
      <Typography className="custom-form-label" variant="caption" display="inline-block" gutterBottom>
        Description
      </Typography>
      <TextField
        className="custom-form-input"
        id="projectDescription"
        type="text"
        size="small"
        variant="outlined"
        rows={4}
        multiline
        fullWidth
        placeholder="Enter Project Description"
        name="description"
        value={values.description || ''}
        error={errors.description && touched.description}
        helperText={touched.description && errors.description}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </div>
  </>
);

const ModalAddProject = ({ openToggle, handleClose, setOpenDialog }) => {
  // init
  const navigate = useNavigate();

  // mutations/queries
  const [addProject] = useMutation(ADD_PROJECT);

  const handleDialogToggle = () => {
    setOpenDialog(!openToggle);
  };

  return (
    <Formik
      initialValues={{ projectName: '', projectKey: '', description: '' }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const variables = {
          input: {
            name: values.projectName,
            key: values.projectKey,
            description: values.description,
            workspaceId: parseInt(sessionStorage.getItem(AUTHORITY), 10),
            isActive: true,
          },
        };
        const { data: { addProject: { id } } } = await addProject({ variables });
        if (id) navigate(`/${PROJECT}/${id}`);
      }}
      enableReinitialize={openToggle}
    >
      {({ handleSubmit, handleChange, handleBlur, touched, errors, values }) => (
        <form action="/project">
          <Modal
            title="Add New Project"
            body={<ModalContent errors={errors} handleBlur={handleBlur} handleChange={handleChange} touched={touched} values={values} />}
            secondaryButton={<Button primary onClick={() => handleSubmit()}>Create Project</Button>}
            primaryButton={<Button primary outlined onClick={handleClose}>Cancel</Button>}
            isOpen={openToggle}
            actionAlignment="right"
            setIsOpen={handleDialogToggle}
          />
        </form>
      )}
    </Formik>
  );
};

ModalContent.propTypes = {
  errors: PropTypes.oneOfType([PropTypes.object]).isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  touched: PropTypes.oneOfType([PropTypes.object]).isRequired,
  values: PropTypes.oneOfType([PropTypes.object]).isRequired,

};

ModalAddProject.propTypes = {
  openToggle: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  setOpenDialog: PropTypes.func.isRequired,
};

export default ModalAddProject;
